// oxlint-disable max-lines
import { AnalyticsBrowser, type EventProperties, type UserTraits } from '@segment/analytics-next';
import { segmentKeys } from '@/constants/common';
import { getRealEnvironment } from '@/utils/environment';
import { screenSizePlugin } from './segment-screen-size-plugin';
import { removeReservationGroupIdPlugin } from './segment-remove-reservation-group-id-plugin';
import { abTestConfigs, getOrAssignTestGroup } from '@/utils/ab-testing';
import { getConsentCategoryState } from './uc-consent';
import { debugSegmentEvent, debugSegmentInitialization } from './segment-debug-logger';
import type { SegmentEventQueueEvent, BeaconEventOptions, SegmentManagerInterface } from '@/typings/segment-manager';
import { getItem } from '@/utils/json-storage';

interface SegmentConsent {
  readonly categoryPreferences: {
    readonly analytics: boolean;
  };
}

interface UcCategory {
  readonly functional?: boolean;
}

interface ConsentChangeEvent {
  readonly ucCategory?: UcCategory;
}

function isCustomEvent(event: unknown): event is CustomEvent<ConsentChangeEvent> {
  return event instanceof CustomEvent && 'detail' in event;
}

export function getSegmentKey(): string {
  const realEnvironment = getRealEnvironment();

  return segmentKeys[realEnvironment];
}

async function getConsentPayload(): Promise<SegmentConsent> {
  const hasAnalyticsConsent = await getConsentCategoryState('Analytics');

  return {
    categoryPreferences: {
      analytics: hasAnalyticsConsent
    }
  };
}

function clearPiiData(): void {
  try {
    const userDataKey = 'ajs_user_traits';
    const groupDataKey = 'ajs_group_properties';
    const userData = getItem(userDataKey);
    const groupData = getItem(groupDataKey);

    if (userData !== null && typeof userData === 'object' && 'email' in userData && userData.email !== undefined) {
      localStorage.removeItem(userDataKey);
    }

    if (groupData !== null && typeof groupData === 'object' &&
      'confirmation_number' in groupData && groupData.confirmation_number !== undefined) {
      localStorage.removeItem(groupDataKey);
    }
  } catch {
    // Silently fail
  }
}

export class SegmentManager implements SegmentManagerInterface {
  private readonly analyticsInstance = new AnalyticsBrowser();
  private analyticsInitialized = false;
  private abTestingInitialized = false;
  private consentChangeCleanup: (() => void) | null = null;
  private beaconUnloadHandlerAttached = false;

  private async loadAnalyticsInstance(): Promise<void> {
    const segmentKey = getSegmentKey();

    await this.analyticsInstance.load({
      writeKey: segmentKey,
      plugins: [
        screenSizePlugin,
        removeReservationGroupIdPlugin
      ]
    }, {
      integrations: {
        'Segment.io': {
          deliveryStrategy: {
            config: {
              keepalive: true
            }
          }
        }
      }
    });

    this.analyticsInitialized = true;
  }

  async initializeAbTesting(): Promise<void> {
    if (this.abTestingInitialized) {
      return;
    }

    debugSegmentInitialization('Initializing A/B testing');
    this.abTestingInitialized = true;

    try {
      const abGroupTraits: Record<string, string> = {};

      for (const config of abTestConfigs) {
        if (config.isActive) {
          const testGroup = getOrAssignTestGroup(config);

          debugSegmentInitialization(`Setting A/B test group: ${config.name} - ${testGroup}`);
          abGroupTraits[config.name] = testGroup;
        }
      }

      if (Object.keys(abGroupTraits).length > 0) {
        await this.identify(null, abGroupTraits);

        debugSegmentInitialization('A/B testing initialized');
      } else {
        debugSegmentInitialization('No A/B tests to initialize');
      }
    } catch (error) {
      debugSegmentInitialization(`Failed to initialize A/B testing: ${String(error)}`);
    }
  }

  reset(): void {
    void this.analyticsInstance.reset();
    clearPiiData();
  }

  async track(
    eventName: string,
    properties?: EventProperties,
    options?: BeaconEventOptions
  ): Promise<void> {
    // Use beacon for critical events
    if (options?.critical === true && SegmentManager.supportsBeacon()) {
      await this.sendViaBeacon('track',
        eventName,
        {
          properties,
          options
        });
      this.attachBeaconUnloadHandler();

      return;
    }

    const analytics = this.getAnalyticsInstance();
    const context = options?.context ?? {};

    context.consent = await getConsentPayload();

    const currentOptions = {
      ...options,
      context
    };

    try {
      await analytics.track(eventName, properties, currentOptions);

      void this.initializeAbTesting();
    } catch {
      // Silently fail to avoid console warnings
    }
  }

  async page(
    pageName: string,
    properties?: EventProperties,
    options?: BeaconEventOptions
  ): Promise<void> {
    const analytics = this.getAnalyticsInstance();
    const context = options?.context ?? {};

    context.consent = await getConsentPayload();

    const currentOptions = {
      ...options,
      context
    };

    try {
      await analytics.page(pageName, properties, currentOptions);

      void this.initializeAbTesting();
    } catch {
      // Silently fail to avoid console warnings
    }
  }

  async identify(
    userId: string | null,
    traits?: UserTraits,
    options?: BeaconEventOptions
  ): Promise<void> {
    const analytics = this.getAnalyticsInstance();
    const context = options?.context ?? {};

    context.consent = await getConsentPayload();

    const currentOptions = {
      ...options,
      context
    };

    debugSegmentEvent('Identify', {
      userId,
      traits
    }, currentOptions);

    try {
      await analytics.identify(userId, traits, currentOptions);
    } catch {
      // Silently fail to avoid console warnings
    }
  }

  async group(
    groupId: string,
    traits?: UserTraits,
    options?: BeaconEventOptions
  ): Promise<void> {
    const analytics = this.getAnalyticsInstance();
    const context = options?.context ?? {};

    context.consent = await getConsentPayload();

    const currentOptions = {
      ...options,
      context
    };

    debugSegmentEvent('Group', {
      groupId,
      traits
    }, currentOptions);

    try {
      await analytics.group(groupId, traits, currentOptions);
    } catch {
      // Silently fail to avoid console warnings
    }
  }

  private processQueuedEvent(event: SegmentEventQueueEvent): void {
    switch (event.type) {
      case 'page': {
        void this.page(event.pageName, event.payload, event.options);

        break;
      }

      case 'track': {
        void this.track(event.eventName, event.payload, event.options);

        break;
      }

      case 'identify': {
        void this.identify(event.userId, event.traits, event.options);

        break;
      }

      case 'group': {
        void this.group(event.groupId, event.traits, event.options);

        break;
      }
    }
  }

  processEventQueue(): void {
    if (window.segmentEventQueue) {
      for (const event of window.segmentEventQueue) {
        this.processQueuedEvent(event);
      }

      // Clear the queue after processing
      window.segmentEventQueue = [];
    }
  }

  private readonly handleConsentChange = (event: unknown): void => {
    if (isCustomEvent(event) === false) {
      return;
    }

    const { ucCategory } = event.detail;

    debugSegmentInitialization('Consent changed');

    if (ucCategory?.functional === true) {
      debugSegmentInitialization('Functional consent change detected, loading analyticsInsance');
      void this.loadAnalyticsInstance();
    } else if (ucCategory?.functional === false && this.analyticsInitialized) {
      debugSegmentInitialization('Analytics consent change detected, clearing analyticsInstance');
      window.location.reload();
    }
  };

  private listenToConsentChange(): () => void {
    debugSegmentInitialization('Listening to consent change');

    window.addEventListener('ucConsentChangedEvent', this.handleConsentChange);

    return () => {
      window.removeEventListener('ucConsentChangedEvent', this.handleConsentChange);
    };
  }

  async initializeWithConsentListening(): Promise<void> {
    if (this.consentChangeCleanup) {
      this.consentChangeCleanup();
    }

    const hasFunctionalConsent = await getConsentCategoryState('Functional');

    if (hasFunctionalConsent) {
      void this.loadAnalyticsInstance();
    }

    this.consentChangeCleanup = this.listenToConsentChange();
  }

  getAnalyticsInstance(): AnalyticsBrowser {
    if (window.analytics && this.isExternalAnalyticsInstance()) {
      debugSegmentInitialization('Using window.analytics instance');

      return window.analytics;
    }

    return this.analyticsInstance;
  }

  private static supportsBeacon(): boolean {
    if (typeof navigator === 'undefined' || !('sendBeacon' in navigator)) {
      return false;
    }

    return true;
  }

  private async sendViaBeacon(
    type: 'track' | 'page' | 'identify' | 'group',
    eventName: string,
    eventData: {
      readonly properties?: EventProperties;
      readonly options?: BeaconEventOptions;
    }
  ): Promise<void> {
    try {
      const segmentKey = getSegmentKey();
      const context = { ...eventData.options?.context };

      context.consent = await getConsentPayload();

      const analytics = this.getAnalyticsInstance();
      // Build payload matching the working Segment format exactly
      const timestamp = new Date().toISOString();

      const basePayload: Record<string, unknown> = {
        timestamp,
        integrations: { 'Segment.io': true },
        type,
        context,
        messageId: `ajs-next-${Date.now()}-${Math.random().toString(36)
          .slice(2, 25)}`,
        writeKey: segmentKey,
        sentAt: timestamp,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        _metadata: {
          bundled: ['Segment.io'],
          unbundled: [],
          bundledIds: []
        }
      };

      // Add user identifiers if available
      try {
        const user = await analytics.user();

        if (user.anonymousId() !== null) {
          basePayload.anonymousId = user.anonymousId();
        }
      } catch {
        // Fallback if user methods not available
      }

      // Build type-specific payload
      let payload = undefined;

      switch (type) {
        case 'track': {
          payload = {
            ...basePayload,
            event: eventName,
            properties: eventData.properties ?? {}
          };
          break;
        }

        case 'page': {
          payload = {
            ...basePayload,
            name: eventName,
            properties: eventData.properties ?? {}
          };
          break;
        }

        case 'identify': {
          payload = {
            ...basePayload,
            userId: eventName,
            traits: eventData.properties ?? {}
          };
          break;
        }

        case 'group': {
          payload = {
            ...basePayload,
            groupId: eventName,
            traits: eventData.properties ?? {}
          };
          break;
        }
      }

      const url = SegmentManager.getSegmentUrl(type);
      const requestData = JSON.stringify(payload);
      // Send via beacon using raw string to match working example
      const isSuccessful = navigator.sendBeacon(url, requestData);

      if (isSuccessful) {
        debugSegmentEvent(`Beacon sent successfully for ${type}`, {
          eventName,
          url,
          payloadSize: requestData.length
        }, eventData.options);
      } else {
        debugSegmentInitialization(`Beacon failed to send for ${type}: ${eventName}`);
      }
    } catch (error) {
      debugSegmentInitialization(`Beacon send error for ${type}: ${String(error)}`);
    }
  }

  private static getSegmentUrl(type: 'track' | 'page' | 'identify' | 'group'): string {
    switch (type) {
      case 'page': {
        return 'https://events.eu1.segmentapis.com/v1/p';
      }

      case 'identify': {
        return 'https://events.eu1.segmentapis.com/v1/i';
      }

      case 'group': {
        return 'https://events.eu1.segmentapis.com/v1/g';
      }

      default: {
        return 'https://events.eu1.segmentapis.com/v1/t';
      }
    }
  }

  private attachBeaconUnloadHandler(): void {
    if (this.beaconUnloadHandlerAttached) {
      return;
    }

    /*
     * Empty implementation - just mark as attached
     * In a real implementation, you could add beforeunload/pagehide handlers here
     */
    this.beaconUnloadHandlerAttached = true;
    debugSegmentInitialization('Beacon unload handler attached');
  }

  private isExternalAnalyticsInstance(): boolean {
    /*
     * Check if window.analytics is from bobw.co (not our own instance)
     * This prevents widgets from using their own analytics when they set window.analytics
     */
    try {
      return window.analytics !== this.analyticsInstance &&
        typeof window.analytics?.track === 'function';
    } catch {
      return false;
    }
  }
}

function isSegmentManagerLike(object: unknown): object is SegmentManager {
  return object !== null &&
    typeof object === 'object' &&
    'track' in object &&
    'page' in object &&
    'identify' in object &&
    'initializeWithConsentListening' in object;
}

export async function initSegmentManager(): Promise<SegmentManager> {
  if (window.segmentManager && isSegmentManagerLike(window.segmentManager)) {
    return window.segmentManager;
  }

  clearPiiData();

  debugSegmentInitialization('Creating new SegmentManager instance');

  const manager = new SegmentManager();

  window.segmentManager = manager;

  await manager.initializeWithConsentListening();

  manager.processEventQueue();

  return manager;
}
